import React, { useState } from 'react';
import { ArrowRightIcon, UserGroupIcon, GlobeAltIcon, XMarkIcon, PencilIcon, CheckIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function TryNowSection() {
  const [formData, setFormData] = useState({
    websiteUrl: 'https://',
    teamMemberCount: 5
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedPage, setGeneratedPage] = useState(null);
  const [extractedColors, setExtractedColors] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const startEditing = (member) => {
    setEditingMember(member.id);
    setEditFormData({
      name: member.name,
      role: member.role,
      avatar: member.avatar
    });
  };

  const saveEdit = () => {
    if (generatedPage) {
      const updatedMembers = generatedPage.teamMembers.map(member => 
        member.id === editingMember 
          ? { ...member, ...editFormData }
          : member
      );
      setGeneratedPage({
        ...generatedPage,
        teamMembers: updatedMembers
      });
    }
    setEditingMember(null);
    setEditFormData({});
  };

  const cancelEdit = () => {
    setEditingMember(null);
    setEditFormData({});
  };

  const extractCompanyName = (url) => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace(/^www\./, '').split('.')[0];
    } catch {
      return 'Company';
    }
  };

  const generateMockTeamMembers = (count) => {
    const names = [
      'Sarah Johnson', 'Michael Chen', 'Emily Rodriguez', 'David Kim', 'Lisa Thompson',
      'James Wilson', 'Maria Garcia', 'Robert Brown', 'Jennifer Lee', 'Christopher Davis',
      'Amanda White', 'Kevin Martinez', 'Rachel Green', 'Daniel Taylor', 'Jessica Moore'
    ];
    
    const roles = [
      'CEO & Founder', 'CTO', 'Head of Design', 'Lead Developer', 'Product Manager',
      'Marketing Director', 'UX Designer', 'Backend Engineer', 'Frontend Developer', 'Data Scientist',
      'Content Strategist', 'Sales Manager', 'Customer Success', 'DevOps Engineer', 'Business Analyst'
    ];

    const avatars = [
      '👩‍💼', '👨‍💻', '👩‍🎨', '👨‍🔧', '👩‍💼',
      '👨‍💼', '👩‍🎨', '👨‍💻', '👩‍💻', '👨‍🔬',
      '👩‍💼', '👨‍💼', '👩‍💼', '👨‍🔧', '👩‍💼'
    ];

    return Array.from({ length: count }, (_, i) => ({
      id: i + 1,
      name: names[i % names.length],
      role: roles[i % roles.length],
      avatar: avatars[i % avatars.length],
      social: {
        linkedin: `https://linkedin.com/in/${names[i % names.length].toLowerCase().replace(' ', '')}`,
        twitter: `https://twitter.com/${names[i % names.length].toLowerCase().replace(' ', '')}`,
        github: `https://github.com/${names[i % names.length].toLowerCase().replace(' ', '')}`
      }
    }));
  };

  const extractColorsFromWebsite = async (url) => {
    try {
      console.log('Attempting to extract colors from:', url);
      
      // Call the actual API endpoint
      const response = await fetch('https://rack51-api.acsrujan.workers.dev', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });
      
      if (response.ok) {
        const colors = await response.json();
        console.log('API returned colors:', colors);
        return colors;
      } else {
        console.log('API request failed:', response.status);
        throw new Error(`API request failed: ${response.status}`);
      }
      
    } catch (error) {
      console.log('Color extraction failed:', error);
      
      // Fallback colors if extraction fails
      return {
        primary: '#EE512D',
        secondary: '#005E77',
        accent: '#f17a5f',
        text: '#333333',
        background: '#ffffff'
      };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsGenerating(true);
    
    try {
      // Extract colors from the website
      const colors = await extractColorsFromWebsite(formData.websiteUrl);
      setExtractedColors(colors);
      
      // Generate mock team members
      const teamMembers = generateMockTeamMembers(parseInt(formData.teamMemberCount));
      
      // Create the generated page data
      const pageData = {
        companyName: extractCompanyName(formData.websiteUrl),
        teamMembers,
        colors,
        websiteUrl: formData.websiteUrl
      };
      
      setGeneratedPage(pageData);
      setShowModal(true);
    } catch (error) {
      console.error('Error generating team page:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
  };

  return (
    <>
      <section className="py-16 px-8 lg:px-12 h-full flex items-center">
        <div className="w-full max-w-lg mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">
              Try It Now
            </h2>
            <p className="text-base text-white/80">
              See how your team page will look with your brand colors.
            </p>
          </div>

          {/* Input Form */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 mb-6 border border-white/20">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="websiteUrl" className="block text-sm font-medium text-white mb-2">
                  <GlobeAltIcon className="inline w-4 h-4 mr-2" />
                  Website URL
                </label>
                <input
                  type="url"
                  id="websiteUrl"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleInputChange}
                  placeholder="https://yourcompany.com"
                  className="w-full px-3 py-2 border border-white/30 rounded-lg focus:ring-2 focus:ring-white focus:border-white bg-white/20 text-white placeholder-white/60 text-sm"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="teamMemberCount" className="block text-sm font-medium text-white mb-2">
                  <UserGroupIcon className="inline w-4 h-4 mr-2" />
                  Number of Team Members
                </label>
                <input
                  type="number"
                  id="teamMemberCount"
                  name="teamMemberCount"
                  value={formData.teamMemberCount}
                  onChange={handleInputChange}
                  min="1"
                  max="50"
                  className="w-full px-3 py-2 border border-white/30 rounded-lg focus:ring-2 focus:ring-white focus:border-white bg-white/20 text-white placeholder-white/60 text-sm"
                  required
                />
              </div>
              
              <div className="text-center">
                <button
                  type="submit"
                  disabled={isGenerating}
                  className="btn-primary w-full py-2 text-sm font-medium rounded-lg focus:ring-4 focus:ring-primary/30 transition-all duration-200 disabled:opacity-50"
                >
                  {isGenerating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      Generate Team Page
                      <ArrowRightIcon className="ml-2 h-4 w-4 inline" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Modal for Generated Team Page */}
      {showModal && generatedPage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Team Page Preview for {generatedPage.companyName}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  Generated from {generatedPage.websiteUrl} • {generatedPage.teamMembers.length} team members
                </p>
              </div>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Extracted Colors Display */}
              {extractedColors && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Colors Extracted from {generatedPage.websiteUrl}</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {Object.entries(extractedColors).map(([key, color]) => (
                      <div key={key} className="text-center">
                        <div 
                          className="w-12 h-12 rounded-lg mx-auto mb-2 border border-gray-200"
                          style={{ backgroundColor: color }}
                        ></div>
                        <div className="text-xs font-medium text-gray-600 capitalize">{key}</div>
                        <div className="text-xs text-gray-500 font-mono">{color}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Team Members Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {generatedPage.teamMembers.map((member) => (
                  <div 
                    key={member.id}
                    className="text-center p-6 rounded-lg border border-gray-200 hover:shadow-md transition-shadow duration-200 relative"
                    style={{ 
                      backgroundColor: generatedPage.colors.background,
                      borderColor: generatedPage.colors.secondary + '20'
                    }}
                  >
                    {/* Edit Button */}
                    <button
                      onClick={() => startEditing(member)}
                      className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white transition-colors duration-200"
                      title="Edit team member"
                    >
                      <PencilIcon className="w-4 h-4 text-gray-600" />
                    </button>

                    {editingMember === member.id ? (
                      /* Edit Mode */
                      <div className="space-y-3">
                        <div className="relative">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Avatar (Emoji)</label>
                          <input
                            type="text"
                            name="avatar"
                            value={editFormData.avatar}
                            onChange={handleEditInputChange}
                            className="w-full px-2 py-1 text-center text-2xl border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="👩‍💼"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                          <input
                            type="text"
                            name="name"
                            value={editFormData.name}
                            onChange={handleEditInputChange}
                            className="w-full px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Full Name"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                          <input
                            type="text"
                            name="role"
                            value={editFormData.role}
                            onChange={handleEditInputChange}
                            className="w-full px-2 py-1 text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Job Title"
                          />
                        </div>
                        
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={saveEdit}
                            className="p-1 rounded-full bg-green-500 hover:bg-green-600 transition-colors duration-200"
                            title="Save changes"
                          >
                            <CheckIcon className="w-4 h-4 text-white" />
                          </button>
                          <button
                            onClick={cancelEdit}
                            className="p-1 rounded-full bg-red-500 hover:bg-red-600 transition-colors duration-200"
                            title="Cancel edit"
                          >
                            <XCircleIcon className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display Mode */
                      <>
                        <div 
                          className="w-20 h-20 rounded-full mx-auto mb-4 text-4xl flex items-center justify-center border-4"
                          style={{ 
                            backgroundColor: generatedPage.colors.primary + '20',
                            borderColor: generatedPage.colors.primary
                          }}
                        >
                          {member.avatar}
                        </div>
                        
                        <h4 
                          className="text-lg font-semibold mb-2"
                          style={{ color: generatedPage.colors.text }}
                        >
                          {member.name}
                        </h4>
                        
                        <p 
                          className="text-sm mb-4"
                          style={{ color: generatedPage.colors.secondary }}
                        >
                          {member.role}
                        </p>
                        
                        <div className="flex justify-center space-x-3">
                          {Object.entries(member.social).map(([platform, url]) => (
                            <a
                              key={platform}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                              style={{ color: generatedPage.colors.primary }}
                            >
                              {platform === 'linkedin' && '🔗'}
                              {platform === 'twitter' && '🐦'}
                              {platform === 'github' && '💻'}
                            </a>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                This is a preview of how your team page will look with extracted brand colors.
              </p>
              <button
                onClick={closeModal}
                className="btn-primary px-6 py-2 text-sm font-medium rounded-lg"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
